import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { ProposalService } from '@core/proposals/proposal.service';
import { SettingsService } from 'app/core/settings/settings.service';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type SendProposalResult =
  | { mode: 'clients'; clientIds: number[] }
  | { mode: 'internal'; internalEmail: string };

export interface SendProposalDialogData {
  clients: Array<{ clientId: number; client: { id: number; name: string } }>;
  /** Proposal reference shown under the title, so the user sees what is being sent. */
  reference?: string | null;
}

/**
 * Standalone recipient-choice dialog for sending a proposal (Task 5 of the
 * import-tax-and-send-recipients plan). Not wired into anything yet: it only closes
 * with a result object describing what the caller (Task 6) should do — either send to
 * a subset of the proposal's linked clients, or to an internal email address instead.
 */
@Component({
  selector: 'app-dialog-send-proposal',
  templateUrl: './dialog-send-proposal.component.html',
  styleUrls: ['./dialog-send-proposal.component.scss'],
})
export class DialogSendProposalComponent implements OnInit {
  mode: 'clients' | 'internal' = 'clients';
  selectedClientIds: number[];
  internalEmail = '';
  errorMessage: string | null = null;
  /** Set while the backend sends proposal emails to a test address instead of the recipients. */
  testRecipient: string | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<DialogSendProposalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SendProposalDialogData,
    private readonly _translocoService: TranslocoService,
    private readonly settingsService: SettingsService,
    private readonly proposalService: ProposalService,
  ) {
    this.selectedClientIds = (data.clients ?? []).map((c) => c.client.id);
  }

  ngOnInit(): void {
    // A failing query only hides the banner; it never blocks sending.
    this.proposalService.getEmailMode().subscribe({
      next: (mode) => (this.testRecipient = mode && !mode.live ? mode.testRecipient : null),
      error: () => (this.testRecipient = null),
    });
    this.settingsService.getDefaultInternalEmail().subscribe((defaultInternalEmail) => {
      if (defaultInternalEmail) {
        this.internalEmail = defaultInternalEmail;
      }
    });
  }

  selectMode(mode: 'clients' | 'internal'): void {
    this.mode = mode;
    this.errorMessage = null;
  }

  toggleClient(clientId: number): void {
    this.selectedClientIds = this.selectedClientIds.includes(clientId)
      ? this.selectedClientIds.filter((id) => id !== clientId)
      : [...this.selectedClientIds, clientId];
  }

  get allClientsSelected(): boolean {
    return (this.data.clients ?? []).every((c) => this.selectedClientIds.includes(c.client.id));
  }

  toggleAllClients(): void {
    this.selectedClientIds = this.allClientsSelected ? [] : (this.data.clients ?? []).map((c) => c.client.id);
  }

  confirm(): void {
    this.errorMessage = null;

    if (this.mode === 'clients') {
      if (this.selectedClientIds.length === 0) {
        this.errorMessage = this._translocoService.translate('proposals.dialogs.sendProposal.noClientSelected');
        return;
      }
      this.dialogRef.close({ mode: 'clients', clientIds: this.selectedClientIds });
      return;
    }

    if (!EMAIL_PATTERN.test(this.internalEmail)) {
      this.errorMessage = this._translocoService.translate('proposals.dialogs.sendProposal.invalidEmail');
      return;
    }
    this.dialogRef.close({ mode: 'internal', internalEmail: this.internalEmail });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
